/** Copyright (c) 2021 T.Rowe Price. All rights reserved. */

import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import reduce from 'lodash/reduce';
import flow from 'lodash/flow';
import startCase from 'lodash/startCase';
import keyBy from 'lodash/keyBy';
import concat from 'lodash/concat';
import partition from 'lodash/partition';
import sortBy from 'lodash/sortBy';
import groupBy from 'lodash/groupBy';
import flatten from 'lodash/flatten';
import numeral from 'numeral';

import {
  formatDollarAmount,
  formatPercentAmount,
  plusMinusValue,
  percentValue
} from 'services/Formats';
import { percentChange } from 'services/Calculation';
import {
  ACTIVE_ADVISORY,
  ACTIVE_PLUS,
  ALASKA_529,
  BROKERAGE,
  isActiveAdvisory,
  isActivePlus,
  isBrokerage,
  MARYLAND_529,
  MUTUAL_FUND,
  NATIONAL_529
} from 'services/Products';

export const filterFunction = (name, account) => {
  const mapping = {
    all: account,
    retirement: account.retirementAccount,
    nonRetirement: !account.retirementAccount,
    owned: account.owner,
    related: account.isRelated,
    viewOnly: account.isViewOnly
  };

  return mapping[name];
};

export const sortingFunction = (name, accounts) => {
  const transformedAccounts = accounts.map((account) => {
    return {
      ...account,
      ownerKey: account.owners.map((v) => v.ownerName).join(', ')
    };
  });
  const [retirementAccounts, nonRetirementAccounts] = partition(
    transformedAccounts,
    (a) => a.retirementAccount
  );
  const sortedRetirementAccounts = sortBy(retirementAccounts, [
    'accountType',
    'ownerKey',
    'accountName'
  ]);
  const sortedNonRetirementAccounts = sortBy(nonRetirementAccounts, [
    'accountType',
    'ownerKey',
    'accountName'
  ]);

  if (name === 'desc') {
    return concat(sortedRetirementAccounts, sortedNonRetirementAccounts);
  }

  if (name === 'asc') {
    return concat(sortedNonRetirementAccounts, sortedRetirementAccounts);
  }

  if (name === 'productType') {
    const productTypeOrder = [
      ACTIVE_PLUS,
      ACTIVE_ADVISORY,
      MUTUAL_FUND,
      BROKERAGE,
      NATIONAL_529,
      MARYLAND_529,
      ALASKA_529
    ];
    let groupedData = groupBy(transformedAccounts, (a) => a.productType);

    for (const group in groupedData) {
      groupedData[group] = sortBy(groupedData[group], [
        'accountType',
        'ownerKey',
        'accountName'
      ]);
    }

    const availableProducts = Object.keys(groupedData)
      .map((a) => parseInt(a))
      .sort(
        (a, b) => productTypeOrder.indexOf(a) - productTypeOrder.indexOf(b)
      );
    return concat([], flatten(availableProducts.map((a) => groupedData[a])));
  }

  if (name === 'ownership') {
    const ownershipOrder = ['Owned', 'Related', 'View Only'];
    let groupedData = groupBy(transformedAccounts, (a) => a.isOwner);

    for (const group in groupedData) {
      groupedData[group] = sortBy(groupedData[group], [
        'accountType',
        'ownerKey',
        'accountName'
      ]);
    }
    const availableOwnership = Object.keys(groupedData).sort(
      (a, b) => ownershipOrder.indexOf(a) - ownershipOrder.indexOf(b)
    );
    return concat([], flatten(availableOwnership.map((a) => groupedData[a])));
  }
};

const remapName = (name) => {
  const mapper = {
    accountType: 'Account Type',
    accountWChecksum: 'Account Number',
    ownerNames: 'Owner(s)',
    difference: 'Daily $ Change',
    percentChange: 'Daily % Change',
    performance: 'PRR'
  };

  return mapper[name] || startCase(name);
};

const remapValue = (name, value) => {
  const mapper = {
    owners: (owners) => owners.map((v) => v.ownerName).join(', '),
    quantity: (v) => (v !== null ? numeral(v).format('0,0.0000') : ''),
    price: (v) => (v !== null ? formatDollarAmount(v) : ''),
    change: (v) =>
      v !== null ? flow(plusMinusValue, formatDollarAmount)(v) : '',
    difference: flow(plusMinusValue, formatDollarAmount),
    percentChange: flow(plusMinusValue, formatPercentAmount),
    marketValue: formatDollarAmount,
    performance: (v) => (v !== null ? percentValue(v) : '')
  };

  return name in mapper ? mapper[name](value) : value;
};

const includeProduct = (productType) =>
  !isBrokerage(productType) &&
  !isActivePlus(productType) &&
  !isActiveAdvisory(productType);

const CURRENT_DATE_COLUMNS = [
  'accountType',
  'accountName',
  'ticker',
  'accountNumber',
  'owners',
  'quantity',
  'price',
  'change',
  'marketValue',
  'difference',
  'percentChange',
  'performance'
];

const HISTORICAL_DATE_COLUMNS = [
  'accountType',
  'accountName',
  'ticker',
  'accountNumber',
  'owners',
  'marketValue'
];

const mapAccount = (account, date) => {
  const acct = {
    ...account,
    ticker:
      !isActivePlus(account.productType) &&
      !isActiveAdvisory(account.productType)
        ? account.ticker
        : null,
    accountNumber: account.accountNumberWChecksum,
    change:
      account.positions && includeProduct(account.productType)
        ? account.positions.reduce((sum, pos) => sum + pos.priceChange, 0)
        : null,
    quantity:
      account.positions && includeProduct(account.productType)
        ? account.positions.reduce((sum, pos) => sum + pos.quantity, 0)
        : null,
    price:
      account.positions && includeProduct(account.productType)
        ? account.positions.reduce((sum, pos) => sum + pos.price, 0)
        : null,
    marketValue: account.balance,
    difference: account.balanceValue - account.previousBalanceValue,
    percentChange: percentChange(
      account.balanceValue,
      account.previousBalanceValue
    )
  };

  const gridColumns = pick(
    acct,
    isNil(date) ? CURRENT_DATE_COLUMNS : HISTORICAL_DATE_COLUMNS
  );

  const accountData = reduce(
    gridColumns,
    (acc, v, k) => ({
      ...acc,
      [remapName(k)]: remapValue(k, v)
    }),
    {}
  );
  return accountData;
};

const mapPerformance = (a, rr) => {
  return {
    ...a,
    performance: rr ? rr.inceptionReturn : null
  };
};

export const getAccountsForExport = (
  accounts = [],
  accountsPerformance = [],
  historicalBalancesDate
) => {
  const performanceByAccountId = keyBy(accountsPerformance, 'accountId');
  const accountsWithPerformance = accounts.map(
    (a) => mapPerformance(a, performanceByAccountId[a.accountId]),
    []
  );
  return accountsWithPerformance.map((account) =>
    mapAccount(account, historicalBalancesDate)
  );
};
